/**
 * Utility functions for checking IBA (Important Bird and Biodiversity Areas) access
 * Based on user's KBA partner affiliation and country's iba_active status
 */

import { 
  IBA_ELIGIBLE_KBA_PARTNERS, 
  IBA_ELIGIBLE_KBA_PARTNER_NAMES,
  IBA_ACCESS_LEVELS,
  IBA_FEATURES,
  IBA_DATA_DISPLAY
} from 'constants/ibaAccess'
import { BIRDLIFE_SECRETARIAT_ROLE_ID } from 'constants/roles'

/**
 * Check if user has IBA access based on their KBA partners and country's iba_active status
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if user has IBA access, false otherwise
 */
export const hasIBAAccess = (profile) => {
  if (!profile || !profile.KbaPartners || !profile.Country) {
    return false
  }

  // Check if user is affiliated with any IBA-eligible KBA partner
  return  profile.KbaPartners.some(partner => 
    IBA_ELIGIBLE_KBA_PARTNERS.includes(partner.id)
  )
}

/**
 * Check if user is from IBA-eligible organizations (regardless of country)
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if user is from eligible organizations
 */
export const isFromIBAEligibleOrganizations = (profile) => {
  if (!profile || !profile.KbaPartners) {
    return false
  }

  return profile.KbaPartners.some(partner => 
    IBA_ELIGIBLE_KBA_PARTNERS.includes(partner.id)
  )
}

/**
 * Check if user's country has IBA active status
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if country has iba_active = true
 */
export const isCountryIBAActive = (profile) => {
  if (!profile || !profile.Country) {
    return false
  }

  return profile.Country.iba_active === true
}

/**
 * Get IBA access level for user
 * @param {Object} profile - User profile object
 * @returns {string} - 'full', 'limited', or 'none'
 */
export const getIBAAccessLevel = (profile) => {
  if (!profile || !profile.KbaPartners || !profile.Country) {
    return 'none'
  }

  const isFromEligibleOrgs = isFromIBAEligibleOrganizations(profile)
  const isCountryActive = isCountryIBAActive(profile)

  if (isFromEligibleOrgs && isCountryActive) {
    return IBA_ACCESS_LEVELS.FULL // Can see all IBA features and data
  } else if (isFromEligibleOrgs && !isCountryActive) {
    return IBA_ACCESS_LEVELS.LIMITED // Can see IBA fields in sites management but with blank/dash data
  } else {
    return IBA_ACCESS_LEVELS.NONE // Cannot see any IBA features
  }
}

/**
 * Check if user has IBA access for birds specifically
 * This is used for species assessments where IBA fields are shown for birds
 * @param {Object} profile - User profile object
 * @param {string} speciesClassName - The class name of the species (e.g., 'AVES')
 * @returns {boolean} - True if user has IBA access for birds, false otherwise
 */
export const hasIBAAccessForBirds = (profile, speciesClassName = 'AVES') => {
  return speciesClassName === 'AVES' && hasIBAAccess(profile)
}

/**
 * Get the list of IBA-eligible KBA partner names
 * @returns {Array} - Array of KBA partner names
 */
export const getIBAEligibleKbaPartnerNames = () => {
  return IBA_ELIGIBLE_KBA_PARTNER_NAMES
}

/**
 * Check if user has access to specific IBA feature
 * @param {Object} profile - User profile object
 * @param {string} feature - IBA feature type
 * @returns {boolean} - True if user has access to the feature
 */
export const hasIBAFeatureAccess = (profile, feature) => {
  if (!profile) {
    return false
  }

  // BirdLife Secretariat: Check if they have CountriesOfInterest
  // They can see IBA features for sites in their countries of interest
  if (profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID) {
    const hasCountriesOfInterest = profile.CountriesOfInterest && 
      Array.isArray(profile.CountriesOfInterest) && 
      profile.CountriesOfInterest.length > 0

    switch (feature) {
      case IBA_FEATURES.SITES_MANAGEMENT:
        // Sites management: Show fields if they have countries of interest
        return hasCountriesOfInterest
      case IBA_FEATURES.CONGREGATIONS:
      case IBA_FEATURES.SPECIES_FIELDS:
      case IBA_FEATURES.CRITERIA:
        // Assessment features: Only for countries of interest that have iba_active
        // This will be checked per-site in site-based access functions
        return hasCountriesOfInterest
      default:
        return false
    }
  }

  // Standard logic for other users
  if (!profile.KbaPartners || !profile.Country) {
    return false
  }

  const isFromEligibleOrgs = isFromIBAEligibleOrganizations(profile)
  const isCountryActive = isCountryIBAActive(profile)

  switch (feature) {
    case IBA_FEATURES.SITES_MANAGEMENT:
      // Sites management: Show fields for eligible orgs regardless of country
      return isFromEligibleOrgs
    case IBA_FEATURES.CONGREGATIONS:
    case IBA_FEATURES.SPECIES_FIELDS:
    case IBA_FEATURES.CRITERIA:
      // Assessment features: Only for eligible orgs in IBA countries
      return isFromEligibleOrgs && isCountryActive
    default:
      return false
  }
}

/**
 * Get IBA data display option for user
 * @param {Object} profile - User profile object
 * @param {string} feature - IBA feature type
 * @returns {string} - Display option (show, dash, hide)
 */
export const getIBADataDisplayOption = (profile, feature) => {
  if (!profile || !profile.KbaPartners || !profile.Country) {
    return IBA_DATA_DISPLAY.HIDE
  }

  const isFromEligibleOrgs = isFromIBAEligibleOrganizations(profile)
  const isCountryActive = isCountryIBAActive(profile)

  if (!isFromEligibleOrgs) {
    return IBA_DATA_DISPLAY.HIDE
  }

  if (feature === IBA_FEATURES.SITES_MANAGEMENT) {
    // Sites management: Show data for IBA countries, dash for non-IBA countries
    return isCountryActive ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.SHOW_DASH
  } else {
    // Assessment features: Only show data for IBA countries
    return isCountryActive ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.HIDE
  }
}
